/**
 * Rate-of-Sale resolver — shared estimation formula (TS side).
 *
 * Given a SKU's REAL per-window rate-of-sale anchor points (windows that have
 * real data; initially 30/60/90, growing over time), resolves the daily
 * rate-of-sale for any target window. Windows without real data are estimated
 * via piecewise linear interpolation between anchors, with slope extrapolation
 * beyond the anchor range (clamped to >= 0).
 *
 * This module is pure and deterministic (no side effects). Its outputs mirror
 * the C# implementation exactly (parity-tested).
 */

/** A single real rate-of-sale anchor: a window (days) mapped to its value. */
export interface RateOfSaleAnchor {
  window: number;
  value: number;
}

/**
 * Accepted anchor input shapes. All are normalized to a de-duplicated,
 * ascending-by-window list before resolution.
 */
export type RateOfSaleAnchors =
  | Map<number, number>
  | Record<number, number>
  | RateOfSaleAnchor[];

/** Result of resolving a rate-of-sale for a target window. */
interface RateOfSaleResult {
  value: number;
  isEstimated: boolean;
}

/**
 * The canonical set of supported rate-of-sale windows (days). Single source of
 * truth for the app layer; kept in sync with the API validator.
 */
export const SUPPORTED_RATE_OF_SALE_WINDOWS: readonly number[] = [
  30, 60, 90,
] as const;

/** Clamp an estimated value to be non-negative. */
function clampNonNegative(value: number): number {
  return Math.max(0, value);
}

/**
 * Normalize any accepted anchor shape into a de-duplicated list sorted by
 * window ascending. Non-finite windows/values are discarded. When the same
 * window appears more than once (array input), the last occurrence wins.
 */
function normalizeAnchors(anchors: RateOfSaleAnchors): RateOfSaleAnchor[] {
  const byWindow = new Map<number, number>();

  const add = (window: number, value: number): void => {
    if (Number.isFinite(window) && Number.isFinite(value)) {
      byWindow.set(window, value);
    }
  };

  if (anchors instanceof Map) {
    for (const [window, value] of anchors) {
      add(window, value);
    }
  } else if (Array.isArray(anchors)) {
    for (const anchor of anchors) {
      add(anchor.window, anchor.value);
    }
  } else {
    for (const [key, value] of Object.entries(anchors)) {
      add(Number(key), value);
    }
  }

  return [...byWindow.entries()]
    .map(([window, value]) => ({ window, value }))
    .toSorted((left, right) => left.window - right.window);
}

/** Linear value along the line through anchors `from` and `to` at `window`. */
function interpolateAt(
  from: RateOfSaleAnchor,
  to: RateOfSaleAnchor,
  window: number
): number {
  const slope = (to.value - from.value) / (to.window - from.window);
  return from.value + (window - from.window) * slope;
}

/**
 * Resolve the daily rate-of-sale for `targetWindow`.
 *
 * Algorithm:
 * 1. Real hit — target window present in anchors → that value, not estimated.
 * 2. Interpolate — target between two anchors → linear interpolation.
 * 3. Extrapolate below — below smallest anchor → slope of two smallest anchors.
 * 4. Extrapolate above — above largest anchor → slope of two largest anchors.
 * 5. Clamp — every estimated value is clamped to >= 0.
 * 6. Exactly 1 anchor — flat = that value for any target.
 * 7. Zero anchors — `dailyRateOfSale` if finite, else 0.
 *
 * `isEstimated` is true whenever the target is not an exact real anchor.
 */
export function resolveRateOfSale(
  anchors: RateOfSaleAnchors,
  dailyRateOfSale: number | undefined,
  targetWindow: number
): RateOfSaleResult {
  const points = normalizeAnchors(anchors);

  // 7. Zero anchors — fall back to dailyRateOfSale (if finite), else 0.
  if (points.length === 0) {
    const fallback =
      dailyRateOfSale !== undefined && Number.isFinite(dailyRateOfSale)
        ? dailyRateOfSale
        : 0;
    return { value: fallback, isEstimated: true };
  }

  // 1. Real hit — exact anchor for the target window.
  const exact = points.find((point) => point.window === targetWindow);
  if (exact) {
    return { value: exact.value, isEstimated: false };
  }

  // 6. Exactly 1 anchor — flat value for any non-exact target.
  if (points.length === 1) {
    const only = points.at(0);
    /* v8 ignore next 3 -- length === 1 guarantees element 0 exists */
    if (only === undefined) {
      return { value: 0, isEstimated: true };
    }
    return { value: clampNonNegative(only.value), isEstimated: true };
  }

  // 2+ anchors: build adjacent segments by iteration so each element types as
  // defined (avoids `noUncheckedIndexedAccess` undefined-ness without non-null
  // assertions). `previous` is undefined only on the first element, so both
  // branches are exercised by any multi-anchor input.
  const segments: [RateOfSaleAnchor, RateOfSaleAnchor][] = [];
  let previous: RateOfSaleAnchor | undefined;
  for (const point of points) {
    if (previous !== undefined) {
      segments.push([previous, point]);
    }
    previous = point;
  }

  const firstSegment = segments.at(0);
  const lastSegment = segments.at(-1);
  /* v8 ignore next 3 -- length >= 2 guarantees at least one segment */
  if (firstSegment === undefined || lastSegment === undefined) {
    return { value: 0, isEstimated: true };
  }

  const [low0, high0] = firstSegment;
  const [lowN, highN] = lastSegment;

  let value: number;
  if (targetWindow < low0.window) {
    // 3. Extrapolate below using the two smallest anchors.
    value = interpolateAt(low0, high0, targetWindow);
  } else if (targetWindow > highN.window) {
    // 4. Extrapolate above using the two largest anchors.
    value = interpolateAt(lowN, highN, targetWindow);
  } else {
    // 2. Interpolate between the bracketing anchors.
    value = low0.value;
    for (const [low, high] of segments) {
      if (targetWindow >= low.window && targetWindow <= high.window) {
        value = interpolateAt(low, high, targetWindow);
        break;
      }
    }
  }

  return { value: clampNonNegative(value), isEstimated: true };
}
